import type {
  AppTenantConfig,
  ConfigAppCoreAuthClientOptions,
  ConfigEntityConfiguration,
  DataPoints,
  DomainEntityConfiguration,
  RulesTemplate,
  TenantStrings,
} from '@web-config-app/core';
import { filterEndpointsForDomainEntity } from '@web-config-app/core';
import {
  commonCoreConfig,
  viteAppEnvironment,
  commonCoreAuthClientOptionsClientId,
  commonCoreAuthClientOptionsDomain,
  commonCoreAuthClientOptionsRedirectUri,
} from '../configs/common/core';
import { mergeTenantStrings } from './merge-tenant-strings';
import { mergeRulesTemplates } from './merge-rules-templates';
import leagueLogo from '../assets/league-logo.svg';
import {
  getGeneratedEnvironmentsConfigForTenant,
  type ResolvedPreviewEnvironment,
} from '../configs/generated/get-generated-environment-config-for-tenant';
import { getGeneratedModuleTreeForTenant } from '../configs/generated/get-generated-module-tree-for-tenant';
import GeneratedEntityConfigs from '../configs/generated/json/entities.json';
import GeneratedTenantConfigs from '../configs/generated/json/tenants.json';
import { GeneratedConfigTenantId } from '../types/config';
import { mergeLocalDataPoints } from './merge-local-data-points';
import { domainEntityConfigModules } from '../configs/domain-entity-config-modules';
import type { EntityId } from '../types/config';

interface CreateTenantConfigArgs {
  tenantId: GeneratedConfigTenantId;
  strings?: TenantStrings;
  rulesTemplates?: RulesTemplate[];
  dataPoints?: DataPoints;
}

const getAuth0ClientOptions = (authConfig: ConfigAppCoreAuthClientOptions) => ({
  ...authConfig.clientOptions,
  domain: authConfig.clientOptions?.domain ?? commonCoreAuthClientOptionsDomain,
  client_id:
    authConfig.clientOptions?.client_id ?? commonCoreAuthClientOptionsClientId,
  redirect_uri:
    authConfig.clientOptions?.redirect_uri ??
    commonCoreAuthClientOptionsRedirectUri,
  useRefreshTokens: true,
});

// JSON import infers overly-narrow literal types for schema shapes that don't
// structurally satisfy EntityDetailSchema; the `unknown` step is required.
const configEntity = GeneratedEntityConfigs[
  'config-entities'
] as unknown as ConfigEntityConfiguration;

type TenantModuleRef = {
  id: string;
  modules?: TenantModuleRef[];
  isUsingConfigAppBackend?: boolean;
};

/**
 * Walks a tenant's module tree and collects entity-level overrides that need
 * to flow into the flat entityConfigs map (e.g. `isUsingConfigAppBackend`,
 * which routes CRUD traffic to a different backend per tenant).
 */
function collectEntityOverrides(
  moduleRefs: TenantModuleRef[],
): Map<string, { isUsingConfigAppBackend?: boolean }> {
  const overrides = new Map<string, { isUsingConfigAppBackend?: boolean }>();
  const visit = (refs: TenantModuleRef[]) => {
    refs.forEach((ref) => {
      if (typeof ref.isUsingConfigAppBackend === 'boolean') {
        overrides.set(ref.id, {
          isUsingConfigAppBackend: ref.isUsingConfigAppBackend,
        });
      }
      if (ref.modules) visit(ref.modules);
    });
  };
  visit(moduleRefs);
  return overrides;
}

/**
 * Builds a flat entity config map from entities.json, injecting runtime-only
 * config (control renderers) that can't be stored in JSON and layering in
 * tenant-level entity overrides from tenants.json.
 */
function buildEntityConfigs(
  tenantId: GeneratedConfigTenantId,
): Record<string, DomainEntityConfiguration> {
  const tenantData = GeneratedTenantConfigs[tenantId];
  // JSON import infers each tenant's `modules` array as a union of concrete
  // literal shapes rather than the recursive `TenantModuleRef[]` interface.
  // Double-cast via `unknown` is required because the two views are
  // structurally incompatible (TS considers them non-overlapping).
  const tenantModuleRefs = (tenantData?.modules ??
    []) as unknown as TenantModuleRef[];
  const entityOverrides = collectEntityOverrides(tenantModuleRefs);

  const entityConfigs: Record<string, DomainEntityConfiguration> = {};

  Object.entries(GeneratedEntityConfigs).forEach(([entityId, entityData]) => {
    if (entityId === 'config-entities') return;
    const runtimeModule = domainEntityConfigModules.get(entityId as EntityId);
    // JSON-inferred entity shape doesn't structurally satisfy `DomainEntityConfiguration`
    // (literal enums, etc.); the generate-configs pipeline enforces the shape at build time,
    // so the runtime assertion is safe.
    const baseEntity = {
      ...entityData,
      ...(runtimeModule?.entityForm?.controlRenderers && {
        controlRenderers: runtimeModule.entityForm.controlRenderers,
      }),
      ...entityOverrides.get(entityId),
    } as DomainEntityConfiguration;

    // `local-server` entities target a dev backend (e.g. localhost:4001 for `example-entity`)
    // and ship their own endpoints; they don't speak the CACT-BE envelope/auth contract. Force
    // `isUsingConfigAppBackend: false` so mutation hooks skip the config-entities wrap and use
    // the entity's endpoints directly.
    if (baseEntity.category === 'local-server') {
      baseEntity.isUsingConfigAppBackend = false;
    }

    // CACT-BE entities don't ship endpoints in entities.json — they declare `operations` and
    // share the global `config-entities` endpoints map. Fill in a filtered subset onto the
    // entity here so runtime CRUD hooks can read `entity.endpoints` directly. Entities with
    // `isUsingConfigAppBackend: false` (non-CACT-BE and local-server) already ship their own
    // endpoints and are left untouched.
    if (baseEntity.isUsingConfigAppBackend !== false) {
      baseEntity.endpoints = filterEndpointsForDomainEntity(
        configEntity.endpoints,
        baseEntity,
      );
    }

    if (!baseEntity.endpoints) {
      // Either a non-CACT-BE entity forgot to declare endpoints, or a CACT-BE entity somehow
      // escaped the fill above. Either way, the runtime contract is violated.
      throw new Error(
        `Entity ${entityId} has no endpoints after buildEntityConfigs — declare them in yaml or set isUsingConfigAppBackend: true.`,
      );
    }

    entityConfigs[entityId] = baseEntity;
  });

  return entityConfigs;
}

/**
 * A preview environment should never resolve to the production key. This check is the
 * enforcement -- a type guard can't be, since Exclude on an open EnvironmentKey is a no-op.
 */
function isNonProductionPreviewEnvironment(
  previewEnvironment: ResolvedPreviewEnvironment,
) {
  return previewEnvironment.environmentKey !== 'production';
}

/**
 * Maps already-resolved preview environments (normalised to a list in
 * get-generated-environment-config-for-tenant.ts) into the client-facing list.
 */
function buildPreviewEnvironments(
  previewEnvironments: ResolvedPreviewEnvironment[],
) {
  const mapped = previewEnvironments
    .filter(isNonProductionPreviewEnvironment)
    .map((resolvedPreviewEnvironment) => ({
      id: resolvedPreviewEnvironment.id,
      apiUrl: resolvedPreviewEnvironment.api.url,
      environmentKey: resolvedPreviewEnvironment.environmentKey,
      label: resolvedPreviewEnvironment.label,
      type: 'preview' as const,
      audience: resolvedPreviewEnvironment.auth0.primaryAudience,
      publishDangerously: resolvedPreviewEnvironment.publishDangerously,
      defaultAuthoringEnvironment:
        resolvedPreviewEnvironment.defaultAuthoringEnvironment,
    }));

  // Two previews sharing an environmentKey would silently collapse in the switcher's Map.
  // Caught at config-build time by validatePreviewEnvironmentKeysAreUnique, not here: a throw
  // during tenant-config construction has no catch before useAccessibleTenants and would take
  // out the switcher for every tenant.
  return mapped.length > 0 ? mapped : undefined;
}

export const createTenantConfig = ({
  tenantId,
  strings,
  rulesTemplates,
  dataPoints,
}: CreateTenantConfigArgs): AppTenantConfig => {
  const { en: enLocale, ...tenantStringsOtherLocales } =
    mergeTenantStrings(strings);

  const tenantDetails = getGeneratedEnvironmentsConfigForTenant(tenantId);
  const modules = getGeneratedModuleTreeForTenant(tenantId);
  const entityConfigs = buildEntityConfigs(tenantId);

  const appEnvironmentAuthoring =
    tenantDetails.appEnvironment[viteAppEnvironment];

  const { baseEnvironment, previewEnvironments, auth0 } =
    appEnvironmentAuthoring;

  const tenantRulesTemplates = mergeRulesTemplates(rulesTemplates);
  const tenantDataPoints = mergeLocalDataPoints(dataPoints);

  const auth = {
    clientOptions: {
      audience: auth0.audience,
      organization: auth0.organizationId,
      domain: auth0.domain,
      client_id: auth0.clientId,
    },
  };
  const auth0ClientOptions = getAuth0ClientOptions(auth);
  return {
    core: {
      ...commonCoreConfig,
      auth: {
        clientOptions: auth0ClientOptions,
        // Number of hours for which a session can be inactive before user is prompted to log out/continue
        // Note: Any changes to this value have to be synced with the idle_session_lifetime in the auth0 configuration.
        idleSessionLifetimeHours: 1,
      },
      appEnvironment: viteAppEnvironment,
      contentUrl: baseEnvironment.api.contentUrl,
      api: {
        wsUrl: baseEnvironment.api.wsUrl,
        url: baseEnvironment.api.url,
        legacyRestApi: baseEnvironment.api.legacyRestApi,
      },
      i18n: {
        strings: {
          default: enLocale,
          en: enLocale,
          ...tenantStringsOtherLocales,
        },
      },
      analytics: {
        GAPropertyId: 'UA-xxxxxxxx-x',
        segmentKey: 'gqIuNkEL7p5hTUQMfKA1R57GSzgL1xFU',
      },
    },
    configApp: {
      tenantId,
      tenantName: tenantDetails.label,
      entityConfigs,
      modules,
      permissionsFilterComplete: false,
      authoring: {
        baseEnvironment: {
          apiUrl: baseEnvironment.api.url,
          environmentKey: baseEnvironment.environmentKey,
          label: baseEnvironment.label,
          id: baseEnvironment.id,
          audience: baseEnvironment.auth0.primaryAudience,
          publishDangerously: baseEnvironment.publishDangerously,
          defaultAuthoringEnvironment:
            baseEnvironment.defaultAuthoringEnvironment,
        },
        previewEnvironments: buildPreviewEnvironments(previewEnvironments),
        disableMultiEnvironmentOperations:
          appEnvironmentAuthoring.disableMultiEnvironmentOperations,
      },
      tenantLogo: leagueLogo,
      frontendValidation: tenantDetails.frontendValidation,
      rulesTemplates: tenantRulesTemplates,
      dataPoints: tenantDataPoints,
      /**
       * Default locale for the config app is 'en-US' which is overridden by the tenant's default locale as returned by the backend.
       */
      defaultLocale: 'en-US',
      configEntity,
    },
  };
};
